import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import TableComponent from "../../common/TableComponent/TableComponent";
import { useNavigate } from "react-router-dom";
import IconButton from "../../common/IconButton/IconButton";
import PlusIcon from "../../../assets/PlusIcon";
import ModalComponent from "../../common/ModalComponent/ModalComponent";
import "./DistributionListManagement.css";
import { toast } from "react-toastify";
import {
  getAllDistributionList,
  updateDistributionList,
  revokeDistributionListEmail,
} from "../../../services/svt/foundation-service";
import Loader from "../../common/Loader/Loader";
import { DataTableComponent } from "../../common/TableComponent/DataTableComponent";
import { useTranslation, Trans } from "react-i18next";

const GroupDetails = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const group = location.state?.group || {};
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isRevokeModalOpen, setIsRevokeModalOpen] = useState(false);
  const [formData, setFormData] = useState({ emails: "" });
  const [selectedMember, setSelectedMember] = useState(null);
  const [groupMembers, setGroupMembers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredMembers, setFilteredMembers] = useState([]);

  const fetchGroupMembers = async () => {
    setLoading(true)
    try {
      const allMembers = await getAllDistributionList();
      if (allMembers.data) {
        const filtered = allMembers.data.filter(
          (entry) => entry.title === group.title
        );

        let emails = [];
        filtered.forEach((item) => {
          if (item.recipientsEmail || item.email) {
            const splitEmails = (item.recipientsEmail || item.email)
              .split(";")
              .map((email) => email.trim())
              .filter(Boolean);
            emails.push(...splitEmails);
          }
        });

        const formatted = emails.map((email) => ({ email }));
        setGroupMembers(formatted);
        setFilteredMembers(formatted);
        setLoading(false);
      }
    } catch (error) {
      console.error("Error fetching members:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (group.title) {
      fetchGroupMembers();
    }
  }, [group.title]);

  const handleSearch = () => {
    const filtered = groupMembers.filter((member) =>
      member.email.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredMembers(filtered);
  };

  const handleOpenModal = () => {
    setFormData({ emails: "", comments: "" });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleInputChange = (target) => {
    setFormData((prev) => ({
      ...prev,
      [target.name]: target.value,
    }));
  };

  // const validateEmails = (emails) => {
  //   const emailArray = emails.split(";").map((email) => email.trim());
  //   return emailArray.filter((email) =>
  //     /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  //   );
  // };

  const validateEmailsWithSemicolons = (emails) => {
    // Trim the input to remove leading/trailing spaces
    const trimmedEmails = emails.trim();

    // If the input is empty, return false
    if (!trimmedEmails) {
      return false;
    }

    // Check for invalid characters immediately following a semicolon (e.g., ;., ;@, etc.)
    // This checks for any non-email-like character after a semicolon
    if (/;[^\w@]/.test(trimmedEmails)) {
      return false; // Invalid character after semicolon (e.g., ;., ;@, etc.)
    }

    // Split emails by semicolon
    const emailArray = trimmedEmails
      .split(";")
      .map((email) => email.trim())
      .filter((email) => email !== ""); // Remove empty entries

    // If there are no emails after splitting, return false
    if (emailArray.length === 0) {
      return false;
    }

    // Check for double semicolons or empty entries in the original string
    if (trimmedEmails.includes(";;")) {
      return false; // Double semicolon found, which means an empty entry
    }

    // Validate each email using a regex
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailArray.every((email) => emailRegex.test(email));
  };

  const handleAddMembers = async () => {
    if (!validateEmailsWithSemicolons(formData.emails)) {
      toast.error(t("invalidEmailFormat"), {
        position: "top-center",
      });
      return;
    }

    setLoading(true);

    // Clean and prepare email list
    const cleanedEmails = formData.emails.trim().endsWith(";")
      ? formData.emails.trim().slice(0, -1)
      : formData.emails.trim();

    const payload = {
      title: group.title,
      description: group.description,
      recipientsEmail: cleanedEmails,
      status: group.isActive,
    };

    try {
      const response = await updateDistributionList(payload, group.id);
      if (response.statusCode === 200) {
        await fetchGroupMembers();
        handleCloseModal();
      }
    } catch (error) {
      console.error("Failed to add members:", error);
      const errorMessage = error?.response?.data?.error;
  toast.error(errorMessage ? t("addMembersError", { error: errorMessage }) : t("addMembersErrorDefault"), { position: "top-center" });
      setIsModalOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenRevokeModal = (row) => {
    setSelectedMember(row);
    setIsRevokeModalOpen(true);
  };

  const handleConfirmRevoke = async () => {
    setLoading(true);

    const email = selectedMember?.email;

    if (!email) {
      toast.error(t("noMemberSelectedToRevoke"), { position: "top-center" });
      return;
    }
    const payload = {
      emails: [email],   
    };
  
    try {
      await revokeDistributionListEmail(group.id, payload);
  toast.success(t("memberRevokedSuccessfully", { email }), { position: "top-center" });
      await fetchGroupMembers();
      setIsRevokeModalOpen(false);
      setSelectedMember(null);
    } catch (error) {
      console.error("Failed to revoke member:", error);
    }
  };

  const handleCancelRevoke = () => {
    setIsRevokeModalOpen(false);
    setSelectedMember(null);
  };

  const columns = [{ name: t("email"), field: "email", selector: (row) => row.email, sortable: true }];

  return (
    <>
      {loading && <Loader />}
      <div className="group-management-container">
        <div className="group-details-header">
          <h2 className="dist-name">
            {t("distributionList")} - {group.title}
          </h2>

          <div className="group-header-buttons">
            <IconButton
              text={t("addMembers")}
              onClick={handleOpenModal}
              style={{
                backgroundColor: "#F0F0F0",
                color: "#454647",
                border: "1px solid #B3B2B4",
              }}
              icon={<PlusIcon />}
            />
            <IconButton
              text={t("close")}
              onClick={() => navigate("/admin/foundation/group-management")} // <-- navigate on click
              style={{
                backgroundColor: "#F0F0F0",
                color: "#454647",
                border: "1px solid #B3B2B4",
              }}
            />
          </div>
        </div>

        <div className="dist-search-bar-container">
          <div className="dist-search-wrapper">
            <input
              type="text"
              placeholder={t("typeEmail")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="dist-search-input"
            />
            {searchTerm && (
              <span
                className="dist-clear-icon"
                onClick={() => {
                  setSearchTerm("");
                  setFilteredMembers(groupMembers);
                }}
              >
                &times;
              </span>
            )}
          </div>
          <button className="dist-search-button" onClick={handleSearch}>
            {t("search")}
          </button>
        </div>

        <DataTableComponent
          columns={columns}
          data={filteredMembers}
          tableName="GroupDetails"
          onRevoke={handleOpenRevokeModal}
          pageCount={5}
        />

        {/* Add Members Modal */}
        <ModalComponent
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          modalTitle={t("addMembers")}
          fieldsData={[
            { fieldName: t("emails"), name: "emails", type: "textarea", required: true, placeholder: t("enterEmailsPlaceholder") },
          ]}
          formData={formData}
          onChangeInput={handleInputChange}
          buttons={[
            <button key="add" className="saveButton" onClick={handleAddMembers}>
              {t("add")}
            </button>,
            <button
              key="cancel"
              className="cancelButton"
              onClick={handleCloseModal}
            >
              {t("cancel")}
            </button>,
          ]}
        />

        {/* Revoke Confirmation Modal */}
        <ModalComponent
          isOpen={isRevokeModalOpen}
          onClose={handleCancelRevoke}
          modalTitle={t("revokeMember")}
          modalText={
            <p>
              <Trans
                i18nKey="revokeConfirm"
                values={{ email: selectedMember?.email }}
                components={{ b: <b /> }}
              />
            </p>
          }
          buttons={[
            <button
              key="revoke"
              className="saveButton"
              onClick={handleConfirmRevoke}
            >
              {t("revoke")}
            </button>,
            <button
              key="cancel"
              className="cancelButton"
              onClick={handleCancelRevoke}
            >
              {t("cancel")}
            </button>,
          ]}
        />
      </div>
    </>
  );
};

export default GroupDetails; 